import { PageProps } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { useIntl } from 'gatsby-plugin-react-intl'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import GridBackground from './assets/grid.svg'
import * as styles from './index.module.scss'
import Career from '../components/career/Career'
import Demo from '../components/demo/Demo'
import Footer from '../components/footer/Footer'
import Header from '../components/header/Header'
import Intro from '../components/intro/Intro'
import Languages from '../components/languages/Languages'
import LevelUp from '../components/level-up/LevelUp'
import Mission from '../components/mission/Mission'
import Outro from '../components/outro/Outro'
import Play from '../components/play/Play'
import Testimonial from '../components/testimonial/Testimonial'
import CookieBanner from '../components/cookie-banner/CookieBanner'

const Index: React.FC<PageProps> = () => {
  const [showCookieBanner, setShowCookieBanner] = useState<boolean>(false)
  const t = useIntl()

  useEffect(() => {
    const storedCookieData: string|null = localStorage.getItem('ngStorage-userProperty-cookiesBanner-codingame')
    const cookieBannerSeen = storedCookieData == null ? false : JSON.parse(storedCookieData)?.seen ?? false
    setShowCookieBanner(!cookieBannerSeen)
  }, [])

  function handleSaveCookies () {
    localStorage.setItem('ngStorage-userProperty-cookiesBanner-codingame', JSON.stringify({ seen: true }))
    setShowCookieBanner(false)
  }

  return (
    <>
      <Helmet>
        <title>{t.formatMessage({ id: 'meta.title' })}</title>
        <meta name='description' data-cg-meta content={t.formatMessage({ id: 'meta.description' })} />
        <meta name='robots' data-cg-meta content='index, follow' />

        <meta property='og:site_name' content='CodinGame' />
        <meta property='og:type' content='website' />
        <meta property='og:title' data-cg-meta content={t.formatMessage({ id: 'meta.title' })} />
        <meta property='og:description' data-cg-meta content={t.formatMessage({ id: 'meta.description' })} />
        <meta property='og:image' data-cg-meta content='https://files.codingame.com/codingame/codingame_share_pics_new.png' />

        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:title' data-cg-meta content={t.formatMessage({ id: 'meta.title' })} />
        <meta name='twitter:description' data-cg-meta content={t.formatMessage({ id: 'meta.description' })} />
        <meta name='twitter:image' data-cg-meta content='https://files.codingame.com/codingame/codingame_share_pics_new.png' />

        <link rel='canonical' href='https://www.codingame.com' />
        <link rel='alternate' hrefLang='x-default' href='https://www.codingame.com/start' />
        <link rel='alternate' hrefLang='fr-fr' href='https://www.example.com/start/fr' />
      </Helmet>
      <div className={styles.mainContainer}>
        <Header />
        <div className={styles.mainWrapper}>
          <GridBackground className={styles.topGrid} />
          <main className={styles.mainContent}>
            <Intro />
            <Demo />
            <Languages />
            <LevelUp />
            <Testimonial
              pic={<StaticImage width={84} height={84} src='./assets/testimonial1.png' alt='' />}
              quote={t.formatMessage({ id: 'testimonials.quote1' })}
              name='Philippe Beaudoin'
              position={t.formatMessage({ id: 'testimonials.position1' })}
            />
            <Play />
            <Testimonial
              pic={<StaticImage width={84} height={84} src='./assets/testimonial2.png' alt='' />}
              quote={t.formatMessage({ id: 'testimonials.quote2' })}
              name='Mary'
              position={t.formatMessage({ id: 'testimonials.position2' })}
            />
            <Career />
            <Testimonial
              pic={<StaticImage width={84} height={84} src='./assets/testimonial3.png' alt='' />}
              quote={t.formatMessage({ id: 'testimonials.quote3' })}
              name='Darren Kent'
              position={t.formatMessage({ id: 'testimonials.position3' })}
            />
            <Mission />
            <Outro />
          </main>
          <GridBackground className={styles.bottomGrid} />
        </div>
        <Footer />
      </div>

      {showCookieBanner && <CookieBanner onSave={handleSaveCookies} />}
    </>
  )
}

export default Index

const localeRedirectScript = `
  function readCookie(cookieName) {
    var re = new RegExp('[; ]'+cookieName+'=([^\\s;]*)')
    var sMatch = document.cookie.match(re)
    if (cookieName && sMatch) {
      return sMatch[1].toLowerCase()
    }
    return null;
  }

  var preferredLanguage = readCookie('cglanguage') ??
    (window.navigator.languages && window.navigator.languages[0]) ??
    window.navigator.language ??
    window.navigator.browserLanguage ??
    window.navigator.userLanguage ??
    window.navigator.systemLanguage ??
    ''

  if (preferredLanguage.toLowerCase().startsWith('fr') && !location.pathname.startsWith('/start/fr')) {
    location.replace('/start/fr')
  }
`

// GTM for KIXELL freelancer
const cgDataLayerScript = `
  var n = document.querySelector('[nonce]');
  var nonce = n.nonce || n.getAttribute('nonce');
  cgDataLayer = [];
  cgDataLayer.push({'nonce': nonce});
  cgDataLayer.push({'site': 'b2c'});
  cgDataLayer.push({'event': 'cgPageview'});
`

const gtmScript = `
  window.loadGtm = function () {
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});
    var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
    var n=d.querySelector('[nonce]');
    n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));
    f.parentNode.insertBefore(j,f);})(window,document,'script','cgDataLayer','GTM-MXDVJH6');
  }
`

export const Head = () => (
  <>
    <title>Coding Games and Programming Challenges to Code Better</title>
    <meta name='description' data-cg-meta content='CodinGame is a challenge-based training platform for programmers where you can play with the hottest programming topics. Solve games, code AI bots, learn from your peers, have fun.' />
    <meta name='robots' data-cg-meta content='index, follow' />

    <meta property='og:site_name' content='CodinGame' />
    <meta property='og:type' content='website' />
    <meta property='og:title' data-cg-meta content='Coding Games and Programming Challenges to Code Better' />
    <meta property='og:description' data-cg-meta content='CodinGame is a challenge-based training platform for programmers where you can play with the hottest programming topics. Solve games, code AI bots, learn from your peers, have fun.' />
    <meta property='og:image' data-cg-meta content='https://files.codingame.com/codingame/codingame_share_pics_new.png' />

    <meta name='twitter:card' content='summary_large_image' />
    <meta name='twitter:title' data-cg-meta content='Coding Games and Programming Challenges to Code Better' />
    <meta name='twitter:description' data-cg-meta content='CodinGame is a challenge-based training platform for programmers where you can play with the hottest programming topics. Solve games, code AI bots, learn from your peers, have fun.' />
    <meta name='twitter:image' data-cg-meta content='https://files.codingame.com/codingame/codingame_share_pics_new.png' />

    <link rel='canonical' href='https://www.codingame.com' />
    <link rel='alternate' hrefLang='x-default' href='https://www.codingame.com/start' />
    <link rel='alternate' hrefLang='fr-fr' href='https://www.example.com/start/fr' />

    <script>
      {localeRedirectScript}
    </script>
    <script nonce='$nonce'>
      {cgDataLayerScript}
    </script>
    <script id='gtmScript' nonce='$nonce'>
      {gtmScript}
    </script>
  </>
)
